import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/Common/Breadcrumb";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetDeploy } from "@/hooks/useDeploys";
import { useGetMetrics } from "@/hooks/useMetrics";
import { PATH } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import { Metric } from "@/types";
import { Activity, ArrowLeft, HardDrive, Zap } from "lucide-react";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/** ======= CONFIGURABLE BUCKET SIZE (minutes) ======= */
const BUCKET_MINUTES = 30; // <— change this if the backend interval changes

function rangeToMs(range: string) {
  switch (range) {
    case "1h":
      return 1 * 60 * 60 * 1000;
    case "24h":
      return 24 * 60 * 60 * 1000;
    case "7d":
      return 7 * 24 * 60 * 60 * 1000;
    case "30d":
      return (
        29 * 24 * 60 * 60 * 1000 +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000
      );
    default:
      return 24 * 60 * 60 * 1000;
  }
}

function floorToBucket(date: Date, bucketMinutes = BUCKET_MINUTES) {
  const ms = date.getTime();
  const bucketMs = bucketMinutes * 60_000;
  return new Date(Math.floor(ms / bucketMs) * bucketMs);
}

function bucketKey(date: Date, bucketMinutes = BUCKET_MINUTES) {
  return floorToBucket(date, bucketMinutes).toISOString();
}

function formatXAxisTick(ts: number, range: string) {
  const d = new Date(ts);
  if (range === "1h" || range === "24h") {
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTooltipTime(ts: number) {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatGigabytes(val?: number) {
  if (!val || val <= 0) return "0 GB";
  const decimals = val >= 100 ? 0 : val >= 10 ? 1 : 2;
  return `${val.toFixed(decimals)} GB`;
}

function formatNumber(n?: number) {
  const v = n ?? 0;
  return v >= 1_000_000
    ? `${(v / 1_000_000).toFixed(1)}M`
    : v >= 1_000
      ? `${(v / 1_000).toFixed(1)}k`
      : `${Math.round(v)}`;
}

export function MetricsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const deployId = searchParams.get("deployId");

  if (!deployId) {
    return (
      <div className="p-8 space-y-4">
        <h1 className="text-4xl font-bold text-cyan-400">Metrics</h1>
        <p className="text-slate-300">
          Choose an application from the Applications page to view its metrics.
        </p>
        <Button
          onClick={() => navigate(PATH.APPLICATIONS)}
          className="cyber-button font-mono w-fit"
          variant="outline"
        >
          Go to Applications
        </Button>
      </div>
    );
  }

  return <MetricsPageContent deployId={deployId} />;
}

function MetricsPageContent({ deployId }: { deployId: string }) {
  const [timeRange, setTimeRange] = useState<"1h" | "24h" | "7d" | "30d">(
    "24h",
  );
  const deployQuery = useGetDeploy(deployId);
  const deploy = deployQuery.data;

  const { startTime, endTime } = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - rangeToMs(timeRange));
    const alignedEnd = floorToBucket(end, BUCKET_MINUTES);
    const alignedStart = floorToBucket(start, BUCKET_MINUTES);
    return { startTime: alignedStart, endTime: alignedEnd };
  }, [timeRange]);
  const navigate = useNavigate();

  const metricsQuery = useGetMetrics(
    deployId,
    startTime,
    endTime,
    Boolean(deployId),
  );

  // Group/normalize into chosen bucket size so UI is decoupled from backend cadence
  const { metricsData, latest, totals } = useMemo(() => {
    const byBucket: Record<
      string,
      {
        ts: number;
        cpu?: number;
        memory?: number;
        requests?: number;
        dataProcessed?: number;
      }
    > = {};

    const rows = (metricsQuery.data as Metric[] | undefined) ?? [];
    for (const m of rows) {
      // Normalize any incoming timestamp to the configured bucket size
      const bStart = floorToBucket(new Date(m.start_time), BUCKET_MINUTES);
      const key = bucketKey(bStart, BUCKET_MINUTES);

      if (!byBucket[key]) byBucket[key] = { ts: bStart.getTime() };

      switch (m.metric_name) {
        case "CpuTotalTime":
          byBucket[key].cpu = m.metric_value;
          break;
        case "MemoryUtilization":
          byBucket[key].memory = m.metric_value;
          break;
        case "RequestCount":
          // additive within bucket
          byBucket[key].requests =
            (byBucket[key].requests ?? 0) + m.metric_value;
          break;
        case "DataProcessedGB":
          // additive within bucket
          byBucket[key].dataProcessed =
            (byBucket[key].dataProcessed ?? 0) + m.metric_value;
          break;
      }
    }

    const arr = Object.values(byBucket).sort((a, b) => a.ts - b.ts);

    const totals = arr.reduce(
      (acc, p) => {
        acc.requests += p.requests ?? 0;
        acc.dataProcessed += p.dataProcessed ?? 0;
        return acc;
      },
      { requests: 0, dataProcessed: 0 },
    );

    const latest = arr[arr.length - 1];

    return { metricsData: arr, latest, totals };
  }, [metricsQuery.data]);

  const cpuNow = latest?.cpu ?? 0;
  const memNow = latest?.memory ?? 0;
  const requestsTotal = totals.requests;
  const dataProcessedTotal = totals.dataProcessed;

  const breadcrumbs = useMemo(() => {
    return [{ label: "metrics" }];
  }, []);

  return (
    <div className="min-h-full">
      <div className="hidden lg:block border-b border-slate-800 bg-slate-900/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <Breadcrumb items={breadcrumbs} />
        </div>
      </div>
      <div className="p-8 space-y-8">
        <div className="mb-8 space-y-2">
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${PATH.DEPLOY}/${deployId}`)}
              className="text-slate-400 hover:text-cyan-400 p-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <div className="text-right">
              <h1 className="font-mono text-cyan-300">
                {deployQuery.data.repoName} /{" "}
                <span className="text-white">Deploy {deployQuery.data.id}</span>
              </h1>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-cyan-400">Metrics</h1>
        </div>

        {/* Filters */}
        <Card className="holo-card">
          <CardContent className="mt-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="flex-1 space-y-2">
                <label className="text-sm font-medium text-cyan-300">
                  Deploy
                </label>
                <div className="grid gap-1 text-sm text-cyan-100 font-mono">
                  <span>Environment: {humanizeString(deploy.env ?? "")}</span>
                  <span>Region: {deploy.region ?? "—"}</span>
                  <span>Branch: {deploy.branch ?? "—"}</span>
                  <span>Status: {humanizeString(deploy.status ?? "")}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block text-cyan-300">
                  Time Range
                </label>
                <Select
                  value={timeRange}
                  onValueChange={(v: "1h" | "24h" | "7d" | "30d") =>
                    setTimeRange(v)
                  }
                >
                  <SelectTrigger className="w-32 bg-slate-800/50 border-cyan-500/30 text-cyan-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="1h" className="text-slate-300">
                      Last 1h
                    </SelectItem>
                    <SelectItem value="24h" className="text-slate-300">
                      Last 24h
                    </SelectItem>
                    <SelectItem value="7d" className="text-slate-300">
                      Last 7d
                    </SelectItem>
                    <SelectItem value="30d" className="text-slate-300">
                      Last 30d
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="holo-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                CPU USAGE (latest)
              </CardTitle>
              <Activity className="h-4 w-4 text-cyan-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-cyan-100">
                {cpuNow.toFixed(1)}%
              </div>
            </CardContent>
          </Card>

          <Card className="holo-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                MEMORY USAGE (latest)
              </CardTitle>
              <HardDrive className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-cyan-100">
                {memNow.toFixed(1)}%
              </div>
            </CardContent>
          </Card>

          <Card className="holo-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                REQUEST COUNT (total)
              </CardTitle>
              <Zap className="h-4 w-4 text-yellow-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-cyan-100">
                {formatNumber(requestsTotal)}
              </div>
              <p className="text-xs text-slate-500">in selected range</p>
            </CardContent>
          </Card>

          <Card className="holo-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                DATA PROCESSED (total)
              </CardTitle>
              <Activity className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-cyan-100">
                {formatGigabytes(dataProcessedTotal)}
              </div>
              <p className="text-xs text-slate-500">in selected range</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* CPU */}
          <Card className="holo-card">
            <CardHeader>
              <CardTitle className="text-cyan-400">CPU Usage</CardTitle>
              <CardDescription className="text-slate-400">
                vCPU utilization over time ({BUCKET_MINUTES}-minute buckets)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metricsData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0, 255, 255, 0.1)"
                  />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    scale="time"
                    tickFormatter={(v) =>
                      formatXAxisTick(v as number, timeRange)
                    }
                    stroke="#64748b"
                    minTickGap={16}
                  />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    labelFormatter={(v) => formatTooltipTime(v as number)}
                    contentStyle={{
                      backgroundColor: "rgba(10, 15, 28, 0.95)",
                      border: "1px solid rgba(0, 255, 255, 0.3)",
                      borderRadius: "8px",
                      color: "#e2e8f0",
                    }}
                    formatter={(value) => [
                      `${(value as number).toFixed(1)}%`,
                      "CPU",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="cpu"
                    stroke="#00ffff"
                    strokeWidth={2}
                    name="CPU %"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Memory */}
          <Card className="holo-card">
            <CardHeader>
              <CardTitle className="text-cyan-400">Memory Usage</CardTitle>
              <CardDescription className="text-slate-400">
                Memory utilization over time ({BUCKET_MINUTES}-minute buckets)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metricsData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0, 255, 255, 0.1)"
                  />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    scale="time"
                    tickFormatter={(v) =>
                      formatXAxisTick(v as number, timeRange)
                    }
                    stroke="#64748b"
                    minTickGap={16}
                  />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    labelFormatter={(v) => formatTooltipTime(v as number)}
                    contentStyle={{
                      backgroundColor: "rgba(10, 15, 28, 0.95)",
                      border: "1px solid rgba(0, 255, 255, 0.3)",
                      borderRadius: "8px",
                      color: "#e2e8f0",
                    }}
                    formatter={(value) => [
                      `${(value as number).toFixed(1)}%`,
                      "Memory",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="memory"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Memory %"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Requests */}
        <Card className="holo-card">
          <CardHeader>
            <CardTitle className="text-cyan-400">Request Count</CardTitle>
            <CardDescription className="text-slate-400">
              Requests per {BUCKET_MINUTES}-minute bucket
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={metricsData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0, 255, 255, 0.1)"
                />
                <XAxis
                  dataKey="ts"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  scale="time"
                  tickFormatter={(v) => formatXAxisTick(v as number, timeRange)}
                  stroke="#64748b"
                  minTickGap={16}
                />
                <YAxis stroke="#64748b" />
                <Tooltip
                  labelFormatter={(v) => formatTooltipTime(v as number)}
                  contentStyle={{
                    backgroundColor: "rgba(10, 15, 28, 0.95)",
                    border: "1px solid rgba(0, 255, 255, 0.3)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                  }}
                  formatter={(value) => [
                    formatNumber(value as number),
                    "Requests",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="requests"
                  stroke="#f59e0b"
                  fill="rgba(245, 158, 11, 0.2)"
                  name="Requests"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Data Processed */}
        <Card className="holo-card">
          <CardHeader>
            <CardTitle className="text-cyan-400">Data Processed</CardTitle>
            <CardDescription className="text-slate-400">
              GB processed per {BUCKET_MINUTES}-minute bucket
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metricsData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0, 255, 255, 0.1)"
                />
                <XAxis
                  dataKey="ts"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  scale="time"
                  tickFormatter={(v) => formatXAxisTick(v as number, timeRange)}
                  stroke="#64748b"
                  minTickGap={16}
                />
                <YAxis
                  stroke="#64748b"
                  tickFormatter={(v) => formatGigabytes(v as number)}
                />
                <Tooltip
                  labelFormatter={(v) => formatTooltipTime(v as number)}
                  contentStyle={{
                    backgroundColor: "rgba(10, 15, 28, 0.95)",
                    border: "1px solid rgba(0, 255, 255, 0.3)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                  }}
                  formatter={(value) => [
                    formatGigabytes(value as number),
                    "Data Processed",
                  ]}
                />
                <Bar
                  dataKey="dataProcessed"
                  fill="#8b5cf6"
                  name="Data Processed"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {metricsQuery.isLoading && (
          <div className="text-sm text-slate-400">Loading metrics…</div>
        )}
        {metricsQuery.isError && (
          <div className="text-sm text-red-400">
            Failed to load metrics. Please change the time range and try again.
          </div>
        )}
        {!metricsQuery.isLoading && metricsData.length === 0 && (
          <div className="text-sm text-slate-400">No data in this range.</div>
        )}
      </div>
    </div>
  );
}
